/**
 * ExecutiveValue — Welcome-page section written for non-technical leaders:
 * Chief Data Officers, Data Governance and Data Quality owners, risk /
 * compliance executives. It replaces the old technical "Trust" panel
 * (tenant isolation / PII redaction / cost ledger) with the outcomes those
 * leaders actually budget for: provable data quality, enforced governance,
 * and audit readiness — in business language, with one concrete example.
 *
 * Every claim maps to a real platform mechanism (kept honest):
 *  - "rules your business agreed on" → SHACL validation gate
 *  - "one approved definition, enforced everywhere" → signed composed packs
 *  - "show the auditor the receipt" → cited, versioned, signed ORB bundles
 *
 * Scoped under `.exec-fx`; the outer <section> inherits the full-width top
 * divider + vertical rhythm from `.welcome-fx section`.
 */

import { useState } from "react";

const OUTCOMES = [
  {
    k: "Data quality",
    h: "Every record checked against rules your business agreed on",
    p: "Completeness, valid values, and consistency are enforced automatically — before data reaches a report, a model, or a customer. Bad records are caught at the door, not discovered in an audit.",
    metric: "Fewer bad records",
    sub: "accuracy · completeness · consistency, enforced",
    control: "SHACL validation gate",
    evidence: "2,481 records checked · 0 critical violations",
    receipt: "DQ-2026-09-29-0841",
  },
  {
    k: "Governance",
    h: "One approved definition, enforced everywhere",
    p: "No more three teams with three meanings of \u201ccustomer.\u201d A definition is proposed, reviewed by the people accountable for it, approved, and then locked so every system uses the same one.",
    metric: "One source of truth",
    sub: "reviewed · approved · version-locked",
    control: "Signed composition pack",
    evidence: "14 definitions approved · version 7 locked",
    receipt: "GOV-PACK-07-41AF",
  },
  {
    k: "Audit & risk",
    h: "Show the auditor the receipt, not a promise",
    p: "Every answer is traceable to the exact approved definition it used, signed and time-stamped. When a regulator asks \u201cwhy did the system decide this?\u201d you have the lineage on hand.",
    metric: "Audit-ready by default",
    sub: "cited · signed · reproducible",
    control: "Decision lineage bundle",
    evidence: "6 sources cited · signature verified",
    receipt: "ORB-193-8C2E",
  },
];

export default function ExecutiveValue() {
  const [activeOutcome, setActiveOutcome] = useState(0);
  const selected = OUTCOMES[activeOutcome];

  return (
    <section id="governance" className="exec-fx">
      <div className="container">
        <div className="section-head">
          <div>
            <div className="section-eyebrow">Governance assurance</div>
            <h2>
              Provable data quality.
              <br />
              Audit-ready by default.
            </h2>
          </div>
          <p className="section-sub">
            Your teams are measured on trustworthy data, defensible decisions,
            and clean audits &mdash; not on ontologies or graphs. Ontoligent turns
            the rules your business already agreed on into controls that run on
            every record, so quality and governance are built in rather than
            chased after the fact.
          </p>
        </div>

        <div className="exec-grid" role="group" aria-label="Governance assurance chain">
          {OUTCOMES.map((o, index) => (
            <button
              key={o.k}
              type="button"
              className={`exec-card ${activeOutcome === index ? "active" : ""}`}
              onClick={() => setActiveOutcome(index)}
              aria-pressed={activeOutcome === index}
            >
              <div className="exec-card-stage">
                <span className="exec-card-no">0{index + 1}</span>
                <span className="exec-card-k">{o.k}</span>
              </div>
              <h3 className="exec-card-h">{o.h}</h3>
              <p className="exec-card-p">{o.p}</p>
              <div className="exec-card-metric">
                <span className="exec-card-metric-v">{o.metric}</span>
                <span className="exec-card-metric-s">{o.sub}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="exec-receipt" aria-live="polite">
          <div className="exec-receipt-index"><span>Assurance receipt</span><strong>0{activeOutcome + 1}</strong></div>
          <div><span>Control executed</span><strong>{selected.control}</strong></div>
          <div><span>Evidence observed</span><strong>{selected.evidence}</strong></div>
          <div><span>Receipt ID</span><strong>{selected.receipt}</strong></div>
          <i>verified</i>
        </div>

 

        {/* ROI strip — where the value shows up on a budget line. */}
        <div className="exec-roi">
          {[
            { v: "Weeks \u2192 minutes", l: "to stand up a governed, validated domain" },
            { v: "Less rework", l: "bad data caught before it spreads downstream" },
            { v: "Faster audits", l: "lineage and sign-off are produced, not reconstructed" },
          ].map((r) => (
            <div key={r.l} className="exec-roi-item">
              <div className="exec-roi-v">{r.v}</div>
              <div className="exec-roi-l">{r.l}</div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .exec-fx {
          --exec-text: var(--text);
          --exec-muted: var(--muted);
          --exec-line: var(--border);
          background-color: hsl(var(--bg));
          background-image:
            linear-gradient(hsl(var(--border) / .2) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--border) / .2) 1px, transparent 1px),
            radial-gradient(700px 280px at 85% 5%, hsl(var(--accent) / .09), transparent 70%);
          background-size: 56px 56px, 56px 56px, auto;
          color: hsl(var(--exec-text));
          border-top-color: hsl(var(--exec-line));
        }
        .exec-fx .container { max-width: 1120px; margin: 0 auto; padding: 0 32px; }
        .exec-fx h2 { color: hsl(var(--exec-text)); }
        .exec-fx .section-sub { color: hsl(var(--exec-muted)); }

        .exec-fx .exec-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; margin-top: 8px;
          border-top: 1px solid hsl(var(--exec-line));
          border-bottom: 1px solid hsl(var(--exec-line));
        }
        @media (max-width: 900px) {
          .exec-fx .exec-grid { grid-template-columns: 1fr; }
        }

        .exec-fx .exec-card {
          position: relative; padding: 28px 26px 26px;
          display: flex; flex-direction: column; min-width: 0;
          border: 0; border-right: 1px solid hsl(var(--exec-line));
          background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer;
          transition: background .2s ease, box-shadow .2s ease; outline: none;
        }
        .exec-fx .exec-card:hover, .exec-fx .exec-card.active { background: hsl(var(--accent) / .07); box-shadow: inset 0 -3px 0 hsl(var(--accent)); }
        .exec-fx .exec-card:focus-visible { box-shadow: inset 0 0 0 2px hsl(var(--accent)); }
        .exec-fx .exec-card:last-child { border-right: 0; }
        @media (max-width: 900px) {
          .exec-fx .exec-card { border-right: 0; border-bottom: 1px solid hsl(var(--exec-line)); }
          .exec-fx .exec-card:last-child { border-bottom: 0; }
        }
        .exec-fx .exec-card-stage { display: flex; align-items: center; gap: 10px; margin-bottom: 15px; }
        .exec-fx .exec-card-no {
          display: grid; place-items: center; width: 28px; height: 28px;
          border: 1px solid hsl(var(--accent)); border-radius: 4px;
          color: hsl(var(--accent-foreground)); background: hsl(var(--accent) / 1);
          font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 10px;
        }
        .exec-fx .exec-card-k {
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em;
          color: hsl(var(--accent));
        }
        .exec-fx .exec-card-h {
          font-size: 17px; font-weight: 650; line-height: 1.3; letter-spacing: -0.01em;
          color: hsl(var(--exec-text)); margin: 0 0 10px;
        }
        .exec-fx .exec-card-p {
          font-size: 13.5px; line-height: 1.6; color: hsl(var(--exec-muted)); margin: 0 0 18px;
        }
        .exec-fx .exec-card-metric {
          margin-top: auto; padding-top: 14px; border-top: 1px solid hsl(var(--exec-line));
          display: flex; flex-direction: column; gap: 3px;
        }
        .exec-fx .exec-card-metric-v { font-size: 15px; font-weight: 650; color: hsl(var(--exec-text)); }
        .exec-fx .exec-card-metric-s {
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 11px; color: hsl(var(--exec-muted));
        }

        .exec-fx .exec-receipt {
          position: relative; display: grid; grid-template-columns: .7fr 1fr 1.2fr .8fr; margin-top: 22px;
          border: 1px solid hsl(var(--accent)); background: hsl(var(--text)); color: hsl(var(--bg));
        }
        .exec-fx .exec-receipt > div { min-width: 0; padding: 16px 18px; border-right: 1px solid hsl(var(--bg) / .2); }
        .exec-fx .exec-receipt span { display: block; margin-bottom: 6px; color: hsl(var(--bg) / .55); font: 9px/1 'IBM Plex Mono', ui-monospace, monospace; letter-spacing: .09em; text-transform: uppercase; }
        .exec-fx .exec-receipt strong { display: block; overflow-wrap: anywhere; color: hsl(var(--bg)); font-size: 12px; font-weight: 500; }
        .exec-fx .exec-receipt-index strong { color: hsl(var(--accent)); font: 24px/1 'IBM Plex Mono', ui-monospace, monospace; }
        .exec-fx .exec-receipt i { position: absolute; right: 10px; top: -10px; padding: 4px 7px; border: 1px solid hsl(var(--accent)); background: hsl(var(--bg)); color: hsl(var(--accent)); font: normal 9px/1 'IBM Plex Mono', ui-monospace, monospace; text-transform: uppercase; }
        @media (max-width: 760px) {
          .exec-fx .exec-receipt { grid-template-columns: 1fr; }
          .exec-fx .exec-receipt > div { border-right: 0; border-bottom: 1px solid hsl(var(--bg) / .2); }
        }

        .exec-fx .exec-example {
          margin-top: 30px; border: 1px solid hsl(var(--exec-line)); border-radius: 8px;
          background: hsl(var(--panel) / .94); padding: 26px 24px;
          box-shadow: 0 18px 48px hsl(var(--text) / .06);
        }
        .exec-fx .exec-eyebrow {
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em;
          color: hsl(var(--exec-muted)); margin-bottom: 8px;
        }
        .exec-fx .exec-example-head h3 {
          font-size: 20px; font-weight: 650; letter-spacing: -0.01em;
          color: hsl(var(--exec-text)); margin: 0 0 18px;
        }
        .exec-fx .exec-example-cols {
          display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
        }
        @media (max-width: 760px) { .exec-fx .exec-example-cols { grid-template-columns: 1fr; } }
        .exec-fx .exec-col {
          border: 1px solid hsl(var(--exec-line)); border-radius: 8px;
          padding: 16px 18px; background: hsl(var(--bg) / 0.72);
        }
        .exec-fx .exec-col-after { border-color: hsl(var(--accent)); background: hsl(var(--accent) / 1); }
        .exec-fx .exec-col-tag {
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.08em;
          color: hsl(var(--exec-muted)); margin-bottom: 10px;
        }
        .exec-fx .exec-col-tag-good { color: hsl(var(--accent-foreground)); }
        .exec-fx .exec-col ul { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 10px; }
        .exec-fx .exec-col li {
          font-size: 13.5px; line-height: 1.55; color: hsl(var(--exec-text));
          padding-left: 18px; position: relative;
        }
        .exec-fx .exec-col-after li { color: hsl(var(--accent-foreground)); }
        .exec-fx .exec-col-before li::before { content: "\\2717"; position: absolute; left: 0; color: hsl(var(--exec-muted)); }
        .exec-fx .exec-col-after li::before { content: "\\2713"; position: absolute; left: 0; color: hsl(var(--accent-foreground)); }
        .exec-fx .exec-example-note {
          font-size: 12px; color: hsl(var(--exec-muted)); margin: 16px 0 0; font-style: italic;
        }

        .exec-fx .exec-roi {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; margin-top: 30px;
          border-top: 1px solid hsl(var(--exec-line)); border-bottom: 1px solid hsl(var(--exec-line));
        }
        @media (max-width: 760px) { .exec-fx .exec-roi { grid-template-columns: 1fr; } }
        .exec-fx .exec-roi-item {
          padding: 20px 22px; text-align: left; border-right: 1px solid hsl(var(--exec-line));
        }
        .exec-fx .exec-roi-item:last-child { border-right: 0; }
        @media (max-width: 760px) {
          .exec-fx .exec-roi-item { border-right: 0; border-bottom: 1px solid hsl(var(--exec-line)); }
          .exec-fx .exec-roi-item:last-child { border-bottom: 0; }
        }
        .exec-fx .exec-roi-v {
          font-size: 19px; font-weight: 700; letter-spacing: -0.01em;
          color: hsl(var(--exec-text)); margin-bottom: 6px;
        }
        .exec-fx .exec-roi-l { font-size: 13px; line-height: 1.5; color: hsl(var(--exec-muted)); }
      `}</style>
    </section>
  );
}
